/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  SmartSession,
  getSession,
  saveSession,
  clearSession,
  isAuthenticated as checkSmartAuth,
} from '@/lib/smartSession';

export interface FHIRUser {
  resourceType: string;
  id: string;
  name?: Array<{
    use?: string;
    text?: string;
    family?: string;
    given?: string[];
    prefix?: string[];
    suffix?: string[];
  }>;
  telecom?: Array<{
    system?: string;
    value?: string;
    use?: string;
  }>;
  address?: Array<{
    use?: string;
    line?: string[];
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }>;
  gender?: string;
  birthDate?: string;
  identifier?: Array<{
    system?: string;
    value?: string;
    type?: {
      text?: string;
    };
  }>;
  active?: boolean;
  // Additional fields can be added based on resource type
  [key: string]: any;
}

export interface PractitionerRole {
  resourceType: string;
  id: string;
  active?: boolean;
  practitioner?: {
    reference?: string;
    display?: string;
  };
  organization?: {
    reference?: string;
    display?: string;
  };
  code?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  specialty?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  [key: string]: any;
}

export interface Organization {
  resourceType: string;
  id: string;
  name?: string;
  active?: boolean;
  type?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  telecom?: Array<{
    system?: string;
    value?: string;
    use?: string;
  }>;
  address?: Array<{
    use?: string;
    line?: string[];
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }>;
  [key: string]: any;
}

interface FHIRContextType {
  session: SmartSession | null;
  fhirUser: FHIRUser | null;
  practitionerRoles: PractitionerRole[];
  organizations: Organization[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  fetchFHIRUser: () => Promise<void>;
  updateSession: (session: SmartSession) => void;
  logout: () => void;
  getDisplayName: () => string | null;
  getUserType: () => string | null;
  getPatientId: () => string | null;
  getPrimaryOrganization: () => Organization | null;
  getPrimaryRole: () => PractitionerRole | null;
  getRoleForOrganization: (organizationId: string) => PractitionerRole | null;
  getRolesForOrganization: (organizationId: string) => PractitionerRole[];
  getOrganizationById: (organizationId: string) => Organization | null;
}

const FHIRContext = createContext<FHIRContextType | undefined>(undefined);

export const useFHIR = () => {
  const context = useContext(FHIRContext);
  if (context === undefined) {
    throw new Error('useFHIR must be used within a FHIRProvider');
  }
  return context;
};

interface FHIRProviderProps {
  children: ReactNode;
}

export const FHIRProvider: React.FC<FHIRProviderProps> = ({ children }) => {
  const [session, setSession] = useState<SmartSession | null>(null);
  const [fhirUser, setFhirUser] = useState<FHIRUser | null>(null);
  const [practitionerRoles, setPractitionerRoles] = useState<PractitionerRole[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize session from storage on mount
  useEffect(() => {
    const loadSession = async () => {
      try {
        const storedSession = getSession();
        if (storedSession) {
          setSession(storedSession);
          // Automatically fetch user details if session exists
          await fetchUserDetails(storedSession);
        }
      } catch (err) {
        console.error('Error loading FHIR session:', err);
        setError(err instanceof Error ? err.message : 'Failed to load session');
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, []);

  /**
   * Fetch FHIR user details from the FHIR server
   */
  const fetchUserDetails = async (sessionData?: SmartSession): Promise<void> => {
    const activeSession = sessionData || session;
    if (!activeSession || !activeSession.fhirUser) {
      console.warn('Cannot fetch user details: missing session or user info');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const baseUrl = activeSession.fhirServer.endsWith('/')
        ? activeSession.fhirServer
        : `${activeSession.fhirServer}/`;

      const headers = {
        Authorization: `${activeSession.tokenType} ${activeSession.accessToken}`,
        Accept: 'application/fhir+json',
      };

      // Fetch user resource
      const userUrl = `${baseUrl}${activeSession.fhirUser}`;

      const response = await fetch(userUrl, { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch user details: ${response.status} ${response.statusText}`);
      }

      const userData: FHIRUser = await response.json();
      setFhirUser(userData);

      // If user is a Practitioner, fetch PractitionerRole with Organization
      if (userData.resourceType === 'Practitioner') {
        await fetchPractitionerRoleAndOrganization(activeSession, userData.id, headers);
      }
    } catch (err) {
      console.error('Error fetching FHIR user:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch user details';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch PractitionerRole and Organization for a Practitioner
   */
  const fetchPractitionerRoleAndOrganization = async (
    activeSession: SmartSession,
    practitionerId: string,
    headers: Record<string, string>
  ): Promise<void> => {
    try {
      const baseUrl = activeSession.fhirServer.endsWith('/')
        ? activeSession.fhirServer
        : `${activeSession.fhirServer}/`;

      // Search for PractitionerRole by practitioner, including Organization
      const roleUrl = `${baseUrl}PractitionerRole?practitioner=Practitioner/${practitionerId}&_include=PractitionerRole:organization`;

      const roleResponse = await fetch(roleUrl, { headers });

      if (!roleResponse.ok) {
        console.warn(`Failed to fetch PractitionerRole: ${roleResponse.status}`);
        return;
      }

      const roleBundle = await roleResponse.json();

      if (roleBundle.resourceType === 'Bundle' && roleBundle.entry) {
        const roles: PractitionerRole[] = [];
        const orgs: Organization[] = [];

        roleBundle.entry.forEach((entry: any) => {
          if (entry.resource) {
            if (entry.resource.resourceType === 'PractitionerRole') {
              roles.push(entry.resource);
            } else if (entry.resource.resourceType === 'Organization') {
              orgs.push(entry.resource);
            }
          }
        });

        setPractitionerRoles(roles);
        setOrganizations(orgs);
      }
    } catch (err) {
      console.error('Error fetching PractitionerRole/Organization:', err);
      // Don't throw - this is supplementary data
    }
  };

  /**
   * Public method to fetch FHIR user (can be called manually)
   */
  const fetchFHIRUser = async (): Promise<void> => {
    await fetchUserDetails();
  };

  /**
   * Update session and persist to storage
   */
  const updateSession = (newSession: SmartSession): void => {
    setSession(newSession);
    saveSession(newSession);
    // Fetch user details for the new session
    fetchUserDetails(newSession);
  };

  /**
   * Logout and clear FHIR session
   */
  const logout = (): void => {
    setSession(null);
    setFhirUser(null);
    setPractitionerRoles([]);
    setOrganizations([]);
    setError(null);
    clearSession();
  };

  /**
   * Get display name from FHIR user
   */
  const getDisplayName = (): string | null => {
    if (!fhirUser) return null;

    // Try to get name from name array
    if (fhirUser.name && fhirUser.name.length > 0) {
      const name = fhirUser.name[0];

      // Use text if available
      if (name.text) return name.text;

      // Otherwise construct from parts
      const parts: string[] = [];
      if (name.prefix) parts.push(...name.prefix);
      if (name.given) parts.push(...name.given);
      if (name.family) parts.push(name.family);
      if (name.suffix) parts.push(...name.suffix);

      if (parts.length > 0) return parts.join(' ');
    }

    // Fallback to resourceType + ID
    return `${fhirUser.resourceType}/${fhirUser.id}`;
  };

  /**
   * Get user type (Practitioner, Patient, etc.)
   */
  const getUserType = (): string | null => {
    return session?.userType || fhirUser?.resourceType || null;
  };

  /**
   * Get patient ID from session (if in patient context)
   */
  const getPatientId = (): string | null => {
    return session?.patient || null;
  };

  /**
   * Get primary organization (first active organization)
   */
  const getPrimaryOrganization = (): Organization | null => {
    if (organizations.length === 0) return null;

    // Try to find an active organization
    const activeOrg = organizations.find(org => org.active !== false);
    return activeOrg || organizations[0];
  };

  /**
   * Get primary role (first active role)
   */
  const getPrimaryRole = (): PractitionerRole | null => {
    if (practitionerRoles.length === 0) return null;

    // Try to find an active role
    const activeRole = practitionerRoles.find(role => role.active !== false);
    return activeRole || practitionerRoles[0];
  };

  /**
   * Get a single role for a specific organization
   * @param organizationId - Organization ID (e.g., "123") or reference (e.g., "Organization/123")
   * @returns The first matching PractitionerRole or null
   */
  const getRoleForOrganization = (organizationId: string): PractitionerRole | null => {
    if (practitionerRoles.length === 0) return null;

    // Normalize the organization ID to just the ID part
    const normalizedId = organizationId.includes('/')
      ? organizationId.split('/').pop()
      : organizationId;

    // Find role where organization reference matches
    const role = practitionerRoles.find(role => {
      const orgRef = role.organization?.reference;
      if (!orgRef) return false;

      const roleOrgId = orgRef.includes('/') ? orgRef.split('/').pop() : orgRef;
      return roleOrgId === normalizedId;
    });

    return role || null;
  };

  /**
   * Get all roles for a specific organization
   * @param organizationId - Organization ID (e.g., "123") or reference (e.g., "Organization/123")
   * @returns Array of matching PractitionerRoles
   */
  const getRolesForOrganization = (organizationId: string): PractitionerRole[] => {
    if (practitionerRoles.length === 0) return [];

    // Normalize the organization ID to just the ID part
    const normalizedId = organizationId.includes('/')
      ? organizationId.split('/').pop()
      : organizationId;

    // Filter roles where organization reference matches
    return practitionerRoles.filter(role => {
      const orgRef = role.organization?.reference;
      if (!orgRef) return false;

      const roleOrgId = orgRef.includes('/') ? orgRef.split('/').pop() : orgRef;
      return roleOrgId === normalizedId;
    });
  };

  /**
   * Get an organization by ID
   * @param organizationId - Organization ID (e.g., "123") or reference (e.g., "Organization/123")
   * @returns The matching Organization or null
   */
  const getOrganizationById = (organizationId: string): Organization | null => {
    if (organizations.length === 0) return null;

    // Normalize the organization ID to just the ID part
    const normalizedId = organizationId.includes('/')
      ? organizationId.split('/').pop()
      : organizationId;

    return organizations.find(org => org.id === normalizedId) || null;
  };

  const value: FHIRContextType = {
    session,
    fhirUser,
    practitionerRoles,
    organizations,
    isAuthenticated: checkSmartAuth(),
    isLoading,
    error,
    fetchFHIRUser,
    updateSession,
    logout,
    getDisplayName,
    getUserType,
    getPatientId,
    getPrimaryOrganization,
    getPrimaryRole,
    getRoleForOrganization,
    getRolesForOrganization,
    getOrganizationById,
  };

  return <FHIRContext.Provider value={value}>{children}</FHIRContext.Provider>;
};
